import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "src/store/store";

export interface ModalStateType {
    isOpen: boolean;
    title: string | null;
    price: number | null;
    discountPrice: number | null;
    image: string | null;
    sku: string | null; // Add sku field
}

const initialState: ModalStateType = {
    isOpen: false,
    title: null,
    price: null,
    discountPrice: null,
    image: null,
    sku: null, // Initialize sku as null
};

export const modalSlice = createSlice({
    name: "modal",
    initialState,
    reducers: {
        openModal: (
            state,
            { payload }: PayloadAction<{ title: string; price: number; discountPrice: number | null; image: string; sku: string }>
        ) => {
            state.isOpen = true;
            state.title = payload.title;
            state.price = payload.price;
            state.discountPrice = payload.discountPrice;
            state.image = payload.image;
            state.sku = payload.sku; // Set sku when modal opens
        },
        closeModal: (state) => {
            state.isOpen = false;
            state.title = null;
            state.price = null;
            state.discountPrice = null;
            state.image = null;
            state.sku = null; // Reset sku when modal closes
        },
        setTitle: (state, { payload }: PayloadAction<string>) => {
            state.title = payload;
        },
        setPrice: (state, { payload }: PayloadAction<number>) => {
            state.price = payload;
        },
        setDiscountPrice: (state, { payload }: PayloadAction<number>) => {
            state.discountPrice = payload;
        },
        setImage: (state, { payload }: PayloadAction<string>) => {
            state.image = payload;
        },
        setSku: (state, { payload }: PayloadAction<string>) => {
            state.sku = payload; // Set sku directly
        },
    },
});

export const { openModal, closeModal, setTitle, setPrice, setDiscountPrice, setImage, setSku } = modalSlice.actions;
export default modalSlice.reducer;

// Selectors
export const modalSelector = (state: RootState) => state.modal;

export const isModalOpenSelector = createSelector(
    [modalSelector],
    (modal) => modal.isOpen
);

export const modalTitleSelector = createSelector(
    [modalSelector],
    (modal) => modal.title
);

export const modalPriceSelector = createSelector(
    [modalSelector],
    (modal) => modal.price
);

export const modalDiscountPriceSelector = createSelector(
    [modalSelector],
    (modal) => modal.discountPrice
);

export const modalImageSelector = createSelector(
    [modalSelector],
    (modal) => modal.image
);

export const modalSkuSelector = createSelector(
    [modalSelector],
    (modal) => modal.sku // Selector for the sku
);